import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/Primitives";
import { BrandMark } from "@/components/Brand";
import { Building2, Home, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";

export default function RoleSelect() {
  const { setRole, user } = useAuth();
  const nav = useNavigate();
  const [picked, setPicked] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Once a role is set on the account it is permanent — block re-entry.
  if (user?.role) return <Navigate to="/dashboard" replace/>;

  const proceed = async () => {
    if (!picked) return;
    setSubmitting(true);
    try {
      await setRole(picked);
      toast.success(`Welcome, ${picked === "landlord" ? "landlord" : "tenant"}!`);
      nav("/dashboard", { replace: true });
    } catch (err) {
      toast.error("Could not set role. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const cards = [
    {
      key: "landlord",
      title: "I'm a landlord",
      description: "I own or manage properties and want to collect rent, screen tenants, and run my rental business.",
      icon: Building2,
      points: ["Properties & units", "Tenant applications", "Lease generation", "Rent collection + reports"],
    },
    {
      key: "tenant",
      title: "I'm a tenant",
      description: "I rent a home and want to pay rent, submit maintenance requests, and access my lease.",
      icon: Home,
      points: ["Pay rent online", "Submit repair tickets", "Download lease docs", "Track rent history"],
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-3xl">
        <div className="flex items-center gap-2.5 justify-center mb-6 sm:mb-10">
          <BrandMark size={36} float/>
          <div className="leading-tight">
            <div className="font-display font-bold text-slate-900 text-sm sm:text-base">Central Rentals</div>
            <div className="text-[10px] sm:text-[11px] tracking-[0.22em] uppercase text-slate-500 font-semibold">Canada</div>
          </div>
        </div>
        <h1 className="text-center font-display text-2xl sm:text-4xl md:text-5xl font-bold tracking-tight text-slate-950 px-2 break-words">
          Which one are you?
        </h1>
        <p className="text-center text-sm sm:text-base text-slate-600 mt-2 sm:mt-3 mb-6 sm:mb-10 px-2">
          Pick your role — this is permanent. Landlord and tenant accounts are kept separate for security.
        </p>

        <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
          {cards.map((c) => {
            const Icon = c.icon;
            const active = picked === c.key;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => setPicked(c.key)}
                data-testid={`role-${c.key}`}
                className={`text-left p-5 sm:p-7 rounded-2xl border-2 transition-all relative
                  ${active ? "border-green-800 bg-white shadow-lg shadow-green-900/10" : "border-slate-200 bg-white hover:border-slate-300"}`}
              >
                {active && (
                  <div className="absolute top-3 right-3 sm:top-4 sm:right-4 w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-green-800 text-white flex items-center justify-center">
                    <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4"/>
                  </div>
                )}
                <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl ${active ? "bg-green-800 text-white" : "bg-green-50 text-green-800"} flex items-center justify-center mb-4 sm:mb-5`}>
                  <Icon className="w-5 h-5 sm:w-6 sm:h-6"/>
                </div>
                <div className="font-display text-base sm:text-xl font-bold text-slate-900">{c.title}</div>
                <p className="text-xs sm:text-sm text-slate-600 mt-1.5 sm:mt-2 leading-relaxed">{c.description}</p>
                <ul className="mt-3 sm:mt-5 space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-slate-700">
                  {c.points.map((p) => (
                    <li key={p} className="flex items-center gap-2 sm:gap-2.5">
                      <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-700 flex-none"/> {p}
                    </li>
                  ))}
                </ul>
              </button>
            );
          })}
        </div>

        <div className="mt-7 sm:mt-9 flex justify-center">
          <Button onClick={proceed} disabled={!picked || submitting} data-testid="role-continue-button" className="px-8 py-3">
            {submitting ? "Setting up…" : <>Continue <ArrowRight className="w-4 h-4"/></>}
          </Button>
        </div>
      </div>
    </div>
  );
}
